import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Brain, ArrowLeft, AlertCircle, Send } from 'lucide-react';
import { getAIAnalysis } from '../../services/analysis';
import { validateAIAnalysis } from '../../utils/aiValidation';
import ReactMarkdown from 'react-markdown';
import { characteristics } from '../../data/characteristics';
import { harmonicLevels } from '../../data/harmonicLevels';
import type { Category } from '../../types';

const analysisButtons = [
  { id: 'intro', label: 'Intro to Report', query: 'Summarize my assessment results, highlighting key insights and themes. Provide a clear overview of my traits, Harmonic Scale position, and areas for personal and professional growth.' },
  { id: 'traits', label: 'Trait Insights', query: 'Analyze all 12 traits in the Being, Doing, and Having categories. Provide insights into each trait, highlighting my scores, how they reflect my current behaviors, and their impact on my daily actions. Identify the most notable traits in each category (high-performing or requiring attention) and explain their role in shaping my personal and professional dynamics.' },
  { id: 'harmonic', label: 'Harmonic Scale', query: 'Explain my position on the Harmonic Scale. How does my emotional expression influence my traits and behaviors? Provide examples of how this interaction impacts my decision-making, relationships, and daily actions' },
  { id: 'strengths', label: 'Key Strengths', query: 'Identify my strongest traits and explain how I can use them to overcome challenges and achieve success. Offer examples of how these strengths can be applied in both personal and professional settings.' },
  { id: 'breakthrough', label: 'Breakthrough Insights', query: 'Break down my Critical, Development, and Growth Zones. Highlight which traits need immediate focus, which ones have potential for steady improvement, and which traits can be leveraged for maximum success.' },
  { id: 'professional', label: 'Professional Insights', query: 'Analyze how my traits manifest in professional contexts, including leadership, teamwork, communication, adaptability, and problem-solving. Provide actionable strategies to enhance my effectiveness in the workplace.' },
  { id: 'relationships', label: 'Relationship Insights', query: 'Provide insights into how my traits and Harmonic Scale position influence my relationships. Offer strategies to improve communication, build trust, and strengthen personal and professional connections.' },
  { id: 'recommendations', label: 'Action Steps', query: 'Based on my assessment, provide clear and specific action steps I can take to improve my traits, elevate my Harmonic Scale position, and achieve my personal and professional goals. Include both short-term and long-term recommendations.' },
];

export function ManualAIAnalysis() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState<Array<{id: string; content: string; sender: 'user' | 'ai'}>>([]);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [harmonicScores, setHarmonicScores] = useState<Record<number, number>>({});
  const [inputMessage, setInputMessage] = useState('');

  const formatDataForAI = (prompt: string) => {
    const categories: Category[] = ['being', 'doing', 'having'];
    const categoryLabels: Record<Category, string> = {
      being: 'BEING',
      doing: 'DOING',
      having: 'HAVING',
    };

    const groupedDetails = categories.map(cat => {
      const charsInCategory = characteristics.filter(c => c.category === cat);
      const lines = charsInCategory.map(char => {
        const score = scores[char.id] || 0;
        return `  - ${char.name}: ${score}%`;
      }).join('\n');
      return `${categoryLabels[cat]}\n${lines}`;
    }).join('\n\n');

    // Get dominant harmonic level
    const harmonicEntries = Object.entries(harmonicScores);

    let dominantHarmonic: [string, number] = ['0', 0];
    let dominantLevel;

    if (harmonicEntries.length > 0) {
      dominantHarmonic = harmonicEntries.reduce((prev, curr) =>
        (curr[1] > prev[1] ? curr : prev), harmonicEntries[0]) as [string, number];
      dominantLevel = harmonicLevels.find(level => level.id === parseInt(dominantHarmonic[0]));
    } else {
      dominantLevel = { name: 'Not Set' };
    }

    // Format all harmonic scores
    const harmonicDetails = harmonicLevels
      .sort((a, b) => (harmonicScores[b.id] || 0) - (harmonicScores[a.id] || 0))
      .map(level => `${level.name}: ${harmonicScores[level.id] || 0}%`)
      .join('\n');

    return `
ASSESSMENT RESULTS (DO NOT modify the categories, characteristic names, or scores below — use them exactly as provided):

Characteristics by Fixed Category:
${groupedDetails}

Harmonic Scale:
Dominant Level: ${dominantLevel?.name} (${dominantHarmonic[1]}%)

Detailed Harmonic Levels:
${harmonicDetails}

IMPORTANT INSTRUCTIONS:
- The three categories (Being, Doing, Having) are FIXED. Each characteristic is already assigned to its correct category. Do NOT reassign, move, or reorganize any characteristic to a different category.
- Use the exact scores shown above. Do NOT estimate, round differently, or invent any scores.
- When referencing a characteristic, always place it under its assigned category shown above.

Based on these results, ${prompt}
`;
  };

  const handleScoreChange = (id: string, value: string, isHarmonic: boolean = false) => {
    const numValue = Math.min(100, Math.max(0, Number(value) || 0));
    if (isHarmonic) {
      setHarmonicScores(prev => ({ ...prev, [id]: numValue }));
    } else {
      setScores(prev => ({ ...prev, [id]: numValue }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    await handleAnalyze(inputMessage);
    setInputMessage('');
  };

  const handleAnalyze = async (prompt: string) => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setError('');
      
      const userMessage = {
        id: Date.now().toString(),
        content: prompt,
        sender: 'user' as const
      };
      setMessages(prev => [...prev, userMessage]);

      const formattedPrompt = formatDataForAI(prompt);
      const response = await getAIAnalysis(formattedPrompt);

      let displayContent = response;
      const scoreList = characteristics.map(char => ({
        characteristicId: char.id,
        raw: scores[char.id] || 0,
        normalized: scores[char.id] || 0,
      }));
      const validation = validateAIAnalysis(response, scoreList);
      if (!validation.valid) {
        displayContent = `${response}\n\n---\n\n**⚠️ Validation Notice:** The following issues were detected in this AI analysis:\n\n${validation.errors.map(e => `- ${e}`).join('\n')}`;
      }

      const aiMessage = {
        id: (Date.now() + 1).toString(),
        content: response,
        sender: 'ai' as const
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Error getting AI analysis:', err);
      setError('Failed to get AI analysis. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link 
          to="/coach" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <Brain className="w-12 h-12 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manual AI Analysis</h1>
            <p className="text-gray-600">Enter scores manually to get AI analysis</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="rounded-2xl p-8 bg-blue-50 border border-blue-100 shadow-sm hover:shadow-md transition-all duration-300">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Characteristic Scores</h2>
            <div className="grid gap-4">
              {characteristics.map(char => (
                <div key={char.id} className="flex items-center gap-4">
                  <label className="text-base font-medium text-gray-900 w-40">{char.name}</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={scores[char.id] || ''}
                    onChange={(e) => handleScoreChange(char.id, e.target.value)}
                    className="w-28 px-4 py-3 rounded-xl bg-white border border-blue-300 
                      focus:border-blue-500 focus:outline-none text-gray-900 shadow-sm"
                    placeholder="0-100"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl p-8 bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-all duration-300">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Harmonic Scores</h2>
            <div className="grid gap-4">
              {[...harmonicLevels].reverse().map(level => (
                <div key={level.id} className="flex items-center gap-4">
                  <label className="text-base font-medium text-gray-900 w-40">{level.name}</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={harmonicScores[level.id] || ''}
                    onChange={(e) => handleScoreChange(level.id.toString(), e.target.value, true)}
                    className="w-28 px-4 py-3 rounded-xl bg-white border border-purple-300 
                      focus:border-purple-500 focus:outline-none text-gray-900 shadow-sm"
                    placeholder="0-100"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl p-8 bg-emerald-50 border border-emerald-100 shadow-sm hover:shadow-md transition-all duration-300 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Analysis</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {analysisButtons.map(button => (
              <button
                key={button.id}
                onClick={() => handleAnalyze(button.query)}
                disabled={isLoading}
                className="px-6 py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 
                  transition-all disabled:opacity-50 text-sm font-medium min-h-[60px] flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105"
              >
                {button.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl p-8 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 shadow-sm hover:shadow-md transition-all duration-300">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">AI Analysis</h2>
          
          <div className="h-[500px] overflow-y-auto custom-scrollbar mb-6 bg-white rounded-xl p-6 border border-indigo-200 shadow-inner">
            {messages.map(message => (
              <div
                key={message.id}
                className={`mb-6 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}
              >
                <div
                  className={`inline-block max-w-[80%] px-8 py-4 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                      : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-900 border border-gray-200 shadow-sm'
                  }`}
                >
                  {message.sender === 'user' ? (
                    message.content
                  ) : (
                    <ReactMarkdown
                      components={{
                        h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-gray-900">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-lg font-bold mb-2 text-gray-900">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-gray-900">{children}</h3>,
                        p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="text-lg text-indigo-600 font-medium animate-pulse">AI is analyzing...</div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex gap-4">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask about these results..."
              className="flex-1 px-6 py-4 rounded-xl bg-white border border-indigo-300 
                focus:border-indigo-500 focus:outline-none text-gray-900 shadow-sm"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !inputMessage.trim()}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 
                text-white font-medium hover:scale-105 transition-all disabled:opacity-50 shadow-lg"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}